/**
 * CruiseHome — CCD-DASH-014 Phase A · Desktop Home ("Planning Studio").
 *
 * The desktop dashboard answers one question:
 *   *"How ready am I for my cruise, and what should I plan next?"*
 *
 * It does NOT duplicate any first-class workspace. Every card is
 * either a decision surface (Readiness · Outstanding Actions ·
 * Milestones) or a launcher (At-a-Glance Rail · Recent Intelligence).
 *
 * Mobile viewport (Execution Companion) is intentionally NOT built
 * yet — that's Phase D. In the interim, this layout stacks vertically
 * on small screens so mobile travelers see a functional (if planning-
 * flavoured) Home until the Mission·Today revival ships.
 *
 * Data flow:
 *   • Voyage identity + At-a-Glance    ← useCruise() context
 *   • Voyage Readiness + Outstanding   ← GET /api/cruises/current/voyage-readiness
 *   • Upcoming Milestones + Activity   ← today.planning_block (existing BFF)
 *
 * Design brief: /app/docs/design/CCD-DASH-014-dashboard-design-sprint.md
 */
import React, { useState, useCallback } from "react";
import { useCruise } from "@/context/CruiseContext";
import { Ship, ArrowRight, Loader2 } from "lucide-react";

import VoyageIdentityStrip from "@/components/cruise-home/VoyageIdentityStrip";
import VoyageReadiness from "@/components/cruise-home/VoyageReadiness";
import OutstandingActions from "@/components/cruise-home/OutstandingActions";
import UpcomingMilestones from "@/components/cruise-home/UpcomingMilestones";
import AtAGlanceRail from "@/components/cruise-home/AtAGlanceRail";
import RecentIntelligence from "@/components/cruise-home/RecentIntelligence";

const CruiseHome = () => {
  const { voyage, today, loading, firstRun, setRoute } = useCruise();
  const [readiness, setReadiness] = useState(null);

  const handleReadinessLoaded = useCallback((data) => {
    setReadiness(data);
  }, []);

  if (loading) {
    return (
      <div
        className="min-h-[60vh] flex items-center justify-center"
        data-testid="voyage-home-loading"
      >
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (firstRun || !voyage) {
    return (
      <div className="max-w-2xl mx-auto mt-16 px-4" data-testid="voyage-home-empty">
        <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-8 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-slate-900 text-white flex items-center justify-center">
              <Ship className="w-5 h-5" />
            </div>
            <div>
              <div className="font-display text-xl font-semibold text-slate-900">
                No active voyage
              </div>
              <div className="font-body text-sm text-slate-600">
                Create or select a cruise to see your Home — readiness, milestones, and planning intelligence.
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setRoute("voyages")}
            data-testid="voyage-home-empty-cta"
            className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
          >
            Open the voyage picker <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  const outstandingActions = readiness?.outstanding_actions || [];
  const recentActivity = today?.planning_block?.recent_activity || [];

  return (
    <div
      className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6"
      data-testid="voyage-home"
    >
      {/* R1 · Voyage Identity Strip */}
      <VoyageIdentityStrip voyage={voyage} />

      {/* R2 · Voyage Readiness™ (flagship) */}
      <VoyageReadiness onDataLoaded={handleReadinessLoaded} />

      {/* R3 · Outstanding Actions ‖ Upcoming Milestones */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <OutstandingActions actions={outstandingActions} />
        <UpcomingMilestones />
      </div>

      {/* R4 · At-a-Glance Rail */}
      <AtAGlanceRail voyage={voyage} readiness={readiness} />

      {/* R5 · Recent Intelligence */}
      <RecentIntelligence activity={recentActivity} />
    </div>
  );
};

export default CruiseHome;
